import { Badge, Button, Drawer, Group, Loader, Modal, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { ArrowLeft } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { useEffect, useMemo, useState, type FC } from 'react';

import { assignB2bPoFc, getB2bPoChannelPos, getB2bPoLines, type B2bPoLine, type B2bPoTracker } from '@api/b2bPo';
import {
    getChannelHealth, getChannelWarehouseOptions,
    type AvailableChannel, type ChannelWarehouseOptions, type DistributionChannelHealth,
} from '@api/distribution';
import DataSheet, { dataSheetTokens, type DataSheetColumn, type DataSheetFilterConfig } from '@components/ui/DataSheet';
import { DatePopover, type UiDateRange } from '@components/ui/DateRange';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import StatCard from '@components/ui/StatCard';
import { neutralColors, semanticColors } from '@styles/design-system';
import { showError } from '@utils/notifications';
import { formatCount, formatMoney } from '@utils/currency';

interface Props {
    channel: AvailableChannel;
    onBack: () => void;
    onCreatePlanFromPo: (channelKey: string, purchaseOrderId: string, destinationWarehouseId: string | null) => void;
}

type PoFilter = 'ALL' | 'OPEN' | 'EXPIRED' | 'ACTED' | 'DISPATCHED' | 'CANCELLED';

/** Grid row: a tracked PO, or (when grouped by FC) a synthetic FC rollup row with its POs as children. */
type PoRow = B2bPoTracker & { isFcGroup?: boolean; fcLabel?: string; subRows?: PoRow[] };

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** yyyy-MM-dd for sort/filter — lexicographic order matches chronological. */
const isoDay = (iso: string | null) => (iso ? iso.split('T')[0] : '');

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

/** Expired = still OPEN, never acted, past the PO expiry date (or closed as EXPIRED by the WMS). */
const isExpired = (po: B2bPoTracker, today: Date): boolean =>
    po.status === 'EXPIRED'
    || (po.status === 'OPEN' && !po.actedAt && po.expiryDate !== null && new Date(po.expiryDate) < today);

const isOpen = (po: B2bPoTracker, today: Date): boolean =>
    po.status === 'OPEN' && !isExpired(po, today);

/** Action status shown in the grid: OPEN rows past expiry surface as EXPIRED. */
const actionLabel = (po: B2bPoTracker, today: Date): string =>
    isExpired(po, today) ? 'EXPIRED' : po.status;

const fcLabelOf = (po: B2bPoTracker): string =>
    po.destinationWarehouseName ?? po.destinationWarehouseId ?? 'Unassigned';

const STATUS_COLOR: Record<string, string> = {
    OPEN: semanticColors.info.primary,
    ACTED: semanticColors.positive.primary,
    DISPATCHED: semanticColors.positive.primary,
    FULFILLED: semanticColors.positive.primary,
    EXPIRED: neutralColors.textMuted,
    CANCELLED: neutralColors.textMuted,
    REJECTED: neutralColors.textMuted,
};

const PO_STATUS_COLOR: Record<string, string> = {
    Created: semanticColors.info.primary,
    Scheduled: semanticColors.info.primary,
    Shipped: semanticColors.positive.primary,
    Fulfilled: semanticColors.positive.primary,
    Cancelled: semanticColors.critical.primary,
    Returned: semanticColors.critical.primary,
    Expired: neutralColors.textMuted,
};

const PoChannelDetailView: FC<Props> = ({ channel, onBack, onCreatePlanFromPo }) => {
    const channelKey = channel.channelAbbreviationKey;
    const isAggregated = channel.config?.isAggregated ?? false;

    const [health, setHealth] = useState<DistributionChannelHealth | null>(null);
    const [pos, setPos] = useState<B2bPoTracker[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<PoFilter>('OPEN');

    // Header-bar controls: entity filters, received-date range, group-by.
    const [poStatusFilter, setPoStatusFilter] = useState<FilterValue>({ poStatus: [] });
    const [fcFilter, setFcFilter] = useState<FilterValue>({ fc: [] });
    const [dateRange, setDateRange] = useState<UiDateRange | null>(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [groupBy, setGroupBy] = useState<string | null>(null);

    // Side panel (line items)
    const [panelPo, setPanelPo] = useState<B2bPoTracker | null>(null);
    const [lines, setLines] = useState<B2bPoLine[] | null>(null);
    const [linesLoading, setLinesLoading] = useState(false);

    // FC picker (per-FC channel + PO without a destination)
    const [fcPo, setFcPo] = useState<B2bPoTracker | null>(null);
    const [fcOptions, setFcOptions] = useState<ChannelWarehouseOptions | null>(null);
    const [fcLoading, setFcLoading] = useState(false);
    const [selectedFc, setSelectedFc] = useState<string | null>(null);
    const [assigning, setAssigning] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getChannelHealth({ channelKey }).then(d => d.channels.find(c => c.channelAbbreviationKey === channelKey) ?? null),
            getB2bPoChannelPos(channelKey),
        ])
            .then(([h, p]) => { setHealth(h); setPos(p); })
            .catch(() => showError('Could not load channel PO data.'))
            .finally(() => setLoading(false));
    }, [channelKey]);

    const today = startOfToday();

    const counts = useMemo(() => ({
        ALL: pos.length,
        OPEN: pos.filter(p => isOpen(p, today)).length,
        EXPIRED: pos.filter(p => isExpired(p, today)).length,
        ACTED: pos.filter(p => p.status === 'ACTED').length,
        DISPATCHED: pos.filter(p => p.status === 'DISPATCHED' || p.status === 'FULFILLED').length,
        CANCELLED: pos.filter(p => p.status === 'CANCELLED' || p.status === 'REJECTED').length,
    }), [pos, today]);

    const visiblePos = useMemo(() => {
        const matchesActionFilter = (p: B2bPoTracker): boolean => {
            switch (filter) {
                case 'ALL': return true;
                case 'OPEN': return isOpen(p, today);
                case 'EXPIRED': return isExpired(p, today);
                case 'ACTED': return p.status === 'ACTED';
                case 'DISPATCHED': return p.status === 'DISPATCHED' || p.status === 'FULFILLED';
                case 'CANCELLED': return p.status === 'CANCELLED' || p.status === 'REJECTED';
            }
        };
        const poStatuses = poStatusFilter.poStatus ?? [];
        const fcs = fcFilter.fc ?? [];
        return pos.filter(p => {
            if (!matchesActionFilter(p)) return false;
            if (poStatuses.length > 0 && !poStatuses.includes(p.poStatus ?? 'Unknown')) return false;
            if (fcs.length > 0 && !fcs.includes(fcLabelOf(p))) return false;
            if (dateRange) {
                const day = isoDay(p.poReceivedAt);
                if (day < dateRange.startDate || day > dateRange.endDate) return false;
            }
            return true;
        });
    }, [pos, filter, today, poStatusFilter, fcFilter, dateRange]);

    // Grouped mode: one expandable rollup row per FC, POs as children (same row type).
    const displayRows = useMemo<PoRow[]>(() => {
        if (groupBy !== 'fc') return visiblePos;
        const groups = new Map<string, PoRow[]>();
        for (const po of visiblePos) {
            const label = fcLabelOf(po);
            const bucket = groups.get(label) ?? [];
            bucket.push(po);
            groups.set(label, bucket);
        }
        return [...groups.entries()]
            .map(([label, subRows]) => ({
                ...subRows[0],
                id: `fc-group:${label}`,
                isFcGroup: true,
                fcLabel: label,
                skuCount: subRows.reduce((s, r) => s + r.skuCount, 0),
                totalUnits: subRows.reduce((s, r) => s + r.totalUnits, 0),
                subRows,
            }))
            .sort((a, b) => {
                // Unassigned is a bucket, not a result — it stays last.
                const aUn = a.fcLabel === 'Unassigned';
                const bUn = b.fcLabel === 'Unassigned';
                if (aUn !== bUn) return aUn ? 1 : -1;
                return a.fcLabel!.localeCompare(b.fcLabel!);
            });
    }, [visiblePos, groupBy]);

    // The FC dimension only exists for per-FC routing. An aggregated channel is
    // distributed to as a whole, so its POs carry no destination FC by design and
    // every FC control (column, filter, group-by, panel line) is dropped.
    const headerFilters = useMemo<DataSheetFilterConfig[]>(() => {
        const poStatusOptions = [...new Set(pos.map(p => p.poStatus ?? 'Unknown'))].sort()
            .map(s => ({ label: s, value: s }));
        const filters: DataSheetFilterConfig[] = [
            {
                label: 'PO status',
                params: [{ key: 'poStatus', label: 'PO status', mode: 'multi', options: poStatusOptions }],
                value: poStatusFilter,
                onChange: setPoStatusFilter,
            },
        ];
        if (!isAggregated) {
            const fcValues = [...new Set(pos.map(fcLabelOf))].sort()
                .map(f => ({ label: f, value: f }));
            filters.push({
                label: 'FC',
                params: [{ key: 'fc', label: 'FC', mode: 'multi', options: fcValues }],
                value: fcFilter,
                onChange: setFcFilter,
            });
        }
        return filters;
    }, [pos, poStatusFilter, fcFilter, isAggregated]);

    const openPanel = (po: B2bPoTracker) => {
        setPanelPo(po);
        setLines(null);
        setLinesLoading(true);
        getB2bPoLines(po.purchaseOrderId)
            .then(setLines)
            .catch(() => setLines([]))
            .finally(() => setLinesLoading(false));
    };

    // Create-plan flow. Per-FC PO with no destination → collect FC first, assign, then open plan.
    const startCreatePlan = async (po: B2bPoTracker, fcWarehouseId?: string) => {
        if (!isAggregated && !po.destinationWarehouseId && !fcWarehouseId) {
            setFcPo(po);
            setSelectedFc(null);
            if (!fcOptions && !fcLoading) {
                setFcLoading(true);
                getChannelWarehouseOptions(channelKey)
                    .then(setFcOptions)
                    .catch(() => setFcOptions(null))
                    .finally(() => setFcLoading(false));
            }
            return;
        }
        if (fcWarehouseId) {
            setAssigning(true);
            try {
                await assignB2bPoFc(po.purchaseOrderId, fcWarehouseId);
            } catch {
                showError(`Could not assign the FC to ${po.poDisplayCode}.`);
                setAssigning(false);
                return;
            }
            setAssigning(false);
            setFcPo(null);
        }
        onCreatePlanFromPo(channelKey, po.purchaseOrderId, fcWarehouseId ?? po.destinationWarehouseId ?? null);
    };

    const columns: DataSheetColumn<PoRow>[] = [
        {
            key: 'poDisplayCode',
            header: 'PO number',
            width: 200,
            align: 'left',
            sticky: true,
            valueAccessor: row => (row.isFcGroup ? row.fcLabel! : row.poDisplayCode),
            render: (_v, row) => (row.isFcGroup ? (
                <Text style={{ fontSize: 13, fontWeight: 600, color: neutralColors.textPrimary }}>
                    {row.fcLabel} · {row.subRows?.length ?? 0} POs
                </Text>
            ) : (
                <Text style={{ fontSize: 13, fontWeight: 600, color: semanticColors.info.primary }}>
                    {row.poDisplayCode}
                </Text>
            )),
        },
        ...(isAggregated ? [] : [{
            key: 'destinationWarehouseName',
            header: 'FC',
            width: 160,
            align: 'left',
            valueAccessor: (row: PoRow) => (row.isFcGroup ? row.fcLabel! : fcLabelOf(row)),
            render: (_v: unknown, row: PoRow) => {
                if (row.isFcGroup) return null;
                const unassigned = !row.destinationWarehouseId;
                return (
                    <Text style={{ fontSize: 13, color: unassigned ? neutralColors.textMuted : neutralColors.textSecondary, fontStyle: unassigned ? 'italic' : undefined }}>
                        {unassigned ? 'Unassigned' : (row.destinationWarehouseName ?? row.destinationWarehouseId)}
                    </Text>
                );
            },
        } satisfies DataSheetColumn<PoRow>]),
        {
            key: 'poReceivedAt',
            header: 'Received',
            width: 120,
            align: 'left',
            valueAccessor: row => (row.isFcGroup ? '' : isoDay(row.poReceivedAt)),
            render: (_v, row) => (row.isFcGroup ? null : (
                <Text style={{ fontSize: 13, color: neutralColors.textSecondary }}>{fmtDate(row.poReceivedAt)}</Text>
            )),
        },
        {
            key: 'expiryDate',
            header: 'Expiry',
            width: 120,
            align: 'left',
            valueAccessor: row => (row.isFcGroup ? '' : (row.expiryDate ?? '')),
            render: (_v, row) => {
                if (row.isFcGroup) return null;
                const expired = isExpired(row, today);
                return (
                    <Text style={{ fontSize: 13, color: expired ? semanticColors.critical.primary : neutralColors.textSecondary }}>
                        {fmtDate(row.expiryDate)}
                    </Text>
                );
            },
        },
        {
            key: 'skuCount',
            header: '# SKUs',
            width: 90,
            align: 'right',
            render: (_v, row) => <Text style={{ fontSize: 13 }}>{row.skuCount}</Text>,
        },
        {
            key: 'totalUnits',
            header: 'Total units',
            width: 110,
            align: 'right',
            render: (_v, row) => <Text style={{ fontSize: 13 }}>{formatCount(row.totalUnits)}</Text>,
        },
        {
            // Before tax, as the partner priced the order - so the higher-value order can be picked first.
            key: 'orderValue',
            header: 'Order value',
            width: 140,
            align: 'right',
            render: (_v, row) => (
                <Text style={{ fontSize: 13 }}>{row.orderValue == null ? '—' : formatMoney(row.orderValue)}</Text>
            ),
        },
        {
            key: 'poStatus',
            header: 'PO status',
            width: 120,
            align: 'center',
            valueAccessor: row => (row.isFcGroup ? '' : (row.poStatus ?? '—')),
            render: (_v, row) => (row.isFcGroup || !row.poStatus ? null : (
                <Badge size="sm" variant="light" color="gray" style={{ color: PO_STATUS_COLOR[row.poStatus] ?? neutralColors.textMuted }}>
                    {row.poStatus}
                </Badge>
            )),
        },
        {
            key: 'status',
            header: 'Action status',
            width: 130,
            align: 'center',
            valueAccessor: row => (row.isFcGroup ? '' : actionLabel(row, today)),
            render: (_v, row) => {
                if (row.isFcGroup) return null;
                const label = actionLabel(row, today);
                return (
                    <Badge size="sm" variant="light" color={row.isBreaching ? 'red' : 'gray'} style={{ color: STATUS_COLOR[label] ?? neutralColors.textMuted }}>
                        {label}
                    </Badge>
                );
            },
        },
        {
            key: 'action',
            header: '',
            width: 130,
            align: 'right',
            sortable: false,
            filterable: false,
            render: (_v, row) => (!row.isFcGroup && isOpen(row, today) ? (
                <Button
                    size="xs"
                    variant="action"
                    onClick={e => { e.stopPropagation(); void startCreatePlan(row); }}
                >
                    Create plan
                </Button>
            ) : null),
        },
    ];

    const dateTrigger = (
        <div style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setShowCalendar(o => !o)}
                title="Filter by received date"
                style={{
                    display: 'flex', alignItems: 'center', gap: 6, height: 30,
                    padding: `0 ${dataSheetTokens.spacing.sm}px`,
                    fontSize: 12, fontWeight: 500,
                    color: dataSheetTokens.colors.textPrimary,
                    backgroundColor: dataSheetTokens.colors.surface,
                    border: `1px solid ${dataSheetTokens.colors.border}`,
                    borderRadius: 6, cursor: 'pointer',
                }}
            >
                <span style={{ fontSize: 12 }}>📅</span>
                <span>
                    {dateRange
                        ? `${format(new Date(dateRange.startDate), 'dd MMM')} – ${format(new Date(dateRange.endDate), 'dd MMM')}`
                        : 'Received: all dates'}
                </span>
                {dateRange && (
                    <span
                        role="button"
                        tabIndex={0}
                        title="Clear the date filter"
                        onClick={e => { e.stopPropagation(); setDateRange(null); }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setDateRange(null); }
                        }}
                        style={{ cursor: 'pointer', fontSize: 11, color: dataSheetTokens.colors.textMuted }}
                    >
                        ✕
                    </span>
                )}
            </button>
            {showCalendar && (
                <DatePopover
                    range={dateRange ?? { startDate: isoDay(new Date().toISOString()), endDate: isoDay(new Date().toISOString()) }}
                    onClose={() => setShowCalendar(false)}
                    onApply={range => { setDateRange(range); setShowCalendar(false); }}
                />
            )}
        </div>
    );

    return (
        <Stack gap={20}>
            <button
                type="button"
                onClick={onBack}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: 13, color: neutralColors.textMuted, padding: 0,
                }}
            >
                <ArrowLeft size={14} /> All PO channels
            </button>

            <div>
                <Text style={{ fontSize: 18, fontWeight: 600, color: neutralColors.textPrimary }}>
                    {channel.channelName || channelKey}
                </Text>
                <Text style={{ fontSize: 12, color: neutralColors.textMuted, marginTop: 2 }}>
                    Incoming POs and their fulfilment against target DoH.
                </Text>
            </div>

            {/* Metric cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                <StatCard label="Channel stock" value={loading ? '—' : formatUnits(health?.totalChannelStock ?? 0)} helper="units at the channel" loading={loading} />
                <StatCard label="Internal available" value={loading ? '—' : formatUnits(health?.internalAvailableUnits ?? 0)} helper="units in internal warehouses" loading={loading} />
                <StatCard label="Open POs" value={loading ? '—' : String(counts.OPEN)} helper="awaiting action" loading={loading} />
                <StatCard
                    label="Critical SKUs"
                    value={loading ? '—' : String(health?.skuCount ?? 0)}
                    helper={`below ${health?.targetDohDays ?? 0}d target DoH`}
                    valueColor={(health?.skuCount ?? 0) > 0 ? semanticColors.critical.primary : undefined}
                    loading={loading}
                />
            </div>

            {/* Action-status filter — the tracker lifecycle (plan created / dispatched / …), outside the sheet. */}
            <Group justify="flex-end">
                <SegmentedControl
                    size="xs"
                    value={filter}
                    onChange={v => setFilter(v as PoFilter)}
                    data={[
                        { value: 'ALL', label: `All (${counts.ALL})` },
                        { value: 'OPEN', label: `Open (${counts.OPEN})` },
                        { value: 'EXPIRED', label: `Expired (${counts.EXPIRED})` },
                        { value: 'ACTED', label: `Acted (${counts.ACTED})` },
                        { value: 'DISPATCHED', label: `Dispatched (${counts.DISPATCHED})` },
                        { value: 'CANCELLED', label: `Cancelled (${counts.CANCELLED})` },
                    ]}
                />
            </Group>

            {/* PO table — standard DataSheet header bar: search, PO-status/FC filters, date, group-by, download. */}
            <DataSheet<PoRow>
                columns={columns}
                data={displayRows}
                isLoading={loading}
                title="Purchase Orders"
                subtitle={groupBy === 'fc' ? `${displayRows.length} FCs · ${visiblePos.length} POs` : `${visiblePos.length} POs`}
                enableSearch
                searchPlaceholder="Search PO number..."
                filters={headerFilters}
                toolbarControls={dateTrigger}
                groupByOptions={isAggregated ? undefined : [{ label: 'FC', value: 'fc' }]}
                activeGroupBy={isAggregated ? null : groupBy}
                onGroupByChange={isAggregated ? undefined : setGroupBy}
                enableDownload
                downloadFilename={`${channelKey}-purchase-orders.csv`}
                maxHeight="auto"
                getRowKey={row => row.id}
                getSubRows={groupBy === 'fc' ? (row => row.subRows) : undefined}
                onRowClick={row => { if (!row.isFcGroup) openPanel(row); }}
            />

            {/* Side panel — PO line items */}
            <Drawer
                opened={panelPo !== null}
                onClose={() => setPanelPo(null)}
                position="right"
                size="md"
                title={panelPo ? `${panelPo.poDisplayCode}` : ''}
                zIndex={9999}
            >
                {panelPo && (
                    <Stack gap="md">
                        <Group gap={24}>
                            <div>
                                <Text size="xs" c="dimmed">Received</Text>
                                <Text size="sm">{fmtDate(panelPo.poReceivedAt)}</Text>
                            </div>
                            <div>
                                <Text size="xs" c="dimmed">Expiry</Text>
                                <Text size="sm">{fmtDate(panelPo.expiryDate)}</Text>
                            </div>
                            {!isAggregated && (
                                <div>
                                    <Text size="xs" c="dimmed">Destination FC</Text>
                                    <Text size="sm">{panelPo.destinationWarehouseId ? (panelPo.destinationWarehouseName ?? panelPo.destinationWarehouseId) : 'Unassigned'}</Text>
                                </div>
                            )}
                            <div>
                                <Text size="xs" c="dimmed">PO status</Text>
                                <Text size="sm">{panelPo.poStatus ?? '—'}</Text>
                            </div>
                            <div>
                                <Text size="xs" c="dimmed">Total units</Text>
                                <Text size="sm">{formatCount(panelPo.totalUnits)}</Text>
                            </div>
                        </Group>

                        <Text style={{ fontSize: 13, fontWeight: 600, color: neutralColors.textPrimary }}>
                            Line items ({panelPo.skuCount})
                        </Text>
                        {linesLoading ? (
                            <Group gap={8}><Loader size="xs" /><Text size="xs" c="dimmed">Loading SKUs…</Text></Group>
                        ) : (lines && lines.length > 0) ? (
                            <Stack gap={6}>
                                {lines.map(l => (
                                    <Group key={l.productId} justify="space-between" wrap="nowrap">
                                        <Text size="sm" style={{ color: neutralColors.textPrimary }}>{l.productName}</Text>
                                        <Text size="sm" style={{ color: neutralColors.textMuted }}>
                                            {l.quantity} units{l.lineTotal == null ? '' : ` · ${formatMoney(l.lineTotal)}`}
                                        </Text>
                                    </Group>
                                ))}
                            </Stack>
                        ) : (
                            <Text size="xs" c="dimmed">No SKU lines on this PO.</Text>
                        )}

                        {isOpen(panelPo, today) && (
                            <Group justify="flex-end" mt={6}>
                                <Button size="xs" variant="action" onClick={() => void startCreatePlan(panelPo)}>
                                    Create plan from this PO
                                </Button>
                            </Group>
                        )}
                    </Stack>
                )}
            </Drawer>

            {/* FC picker */}
            <Modal
                opened={fcPo !== null}
                onClose={() => setFcPo(null)}
                title={fcPo ? `Select an FC for ${fcPo.poDisplayCode}` : ''}
                centered
                zIndex={10000}
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        This channel routes per FC but the PO has no destination warehouse. Pick the FC to
                        fulfil this PO from — it will be saved onto the PO and pre-selected in the plan settings.
                    </Text>
                    {fcLoading ? (
                        <Group gap={8}><Loader size="xs" /><Text size="sm" c="dimmed">Loading FCs…</Text></Group>
                    ) : (
                        <Select
                            label="Destination FC"
                            placeholder="Choose a warehouse"
                            searchable
                            data={(fcOptions?.channelWarehouses ?? []).map(w => ({ value: w.warehouseId, label: w.name }))}
                            value={selectedFc}
                            onChange={setSelectedFc}
                        />
                    )}
                    <Group justify="flex-end" gap="xs">
                        <Button size="xs" variant="subtle" onClick={() => setFcPo(null)}>Cancel</Button>
                        <Button
                            size="xs"
                            variant="action"
                            loading={assigning}
                            disabled={!selectedFc}
                            onClick={() => fcPo && selectedFc && void startCreatePlan(fcPo, selectedFc)}
                        >
                            Assign FC &amp; continue
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

export default PoChannelDetailView;
